import { autoinject } from "aurelia-dependency-injection";
import { useView } from "aurelia-framework";
import KeyDownEvent = JQuery.KeyDownEvent;
import { BindingEngine, computedFrom, observable } from "aurelia-binding";
import { CustomGridPageBase } from "../../../../../../common/custom-grid-page.base";
import { I18N } from "aurelia-i18n";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { RightShipDialogServiceConstants } from "../../../../../../../../constants/basics/rightship-dialog.constants";
import { DataGridColumn, DataType, ItemSource, SourceType, QueryFilter } from "../../../../../../../../models/queries/queries.models";
import { CustomerSubscriptionService } from "../../../../../../../../services/modules/customer-subscription-client.service";
import { CustomerSubscriptionModel, SubscriptionProductCodes, SubscriptionTypeEnum } from "../../../../../../../../models/customers/customer-subscription.models";
import { MobileHelper } from "../../../../../../../../helpers/mobile-helper";
import * as moment from "moment";
import { SubscriptionHelper } from "../../../../helpers/subscription-helper";
import { NotificationClientService, NotificationType } from "@rightship-tech/common-ui/services/basic/notification-client.service";
import { RightshipConfirmationDialogModel } from "../../../../../../../dialogs/rightship-confirmation-dialog/rightship-confirmation-dialog";
import { CustomerVesselService } from "../../../../../../../../services/vessels/customer-vessels.service";
import { ValidationsClientService } from "../../../../../../../../services/validations/validations-client.service";

@autoinject
@useView('views/pages/modules/my-subscription/tabs/information-services/tabs/my-vessels/my-vessels.html')
export class MyVessels extends CustomGridPageBase {

    private _subscriptionProductCode: string;
    public itemSource: ItemSource = null;
    public customerSubscription: CustomerSubscriptionModel;
    public initialFilters: QueryFilter[] = [];
    public isLimitedSubscription: boolean;

    public customerId: number;
    private _reloadSubscription: () => void;
    public isRightShipUser: boolean;

    @computedFrom('customerId')
    get currentCustomerId() {
        if(this.customerId){
            this.setInitialFilters();
            this.itemSource = this.getItemSource();
        }
        return this.customerId;
    }

    @observable()
    filter: string;

    constructor(i18N: I18N, bindingEngine: BindingEngine,
        private dialogClientService: DialogClientService,
        private customerSubscriptionService: CustomerSubscriptionService, 
        private subscriptionHelper: SubscriptionHelper,
        private customerVesselsService: CustomerVesselService,
        private notificationService: NotificationClientService,
        private validationsClientService: ValidationsClientService) {
        super(i18N, 'Vessels', bindingEngine);
    }

    @computedFrom("dataGridVM.unfilteredCount")
    get numOfVessels() {
        if (this.dataGridVM && this.dataGridVM.unfilteredCount && this.dataGridVM.unfilteredCount > 0)
            return this.dataGridVM.unfilteredCount;
        return 0;
    }

    //Get subscription end date
    @computedFrom("customerSubscription")
    get subscriptionEndDate() {
        if(this.customerSubscription?.doesNotExpire) {
            return "Does not expire";
        }
        else if(this.customerSubscription?.subscriptionEndDate) {
            return moment(this.customerSubscription.subscriptionEndDate).format('DD MMM YYYY');
        }
        else {
            return "N/A";
        }
    }

    @computedFrom("customerSubscription", "customerSubscription.isExpired", "customerSubscription.metadata.currentVesselsCount", "customerSubscription.metadata.maxVesselsCount")
    public get canAddVessels() {
        return this.customerSubscription && !this.customerSubscription.isExpired && this.customerSubscription.metadata.currentVesselsCount < this.customerSubscription.metadata.maxVesselsCount;
    }

    @computedFrom("customerSubscription", "customerSubscription.isExpired", "customerSubscription.metadata.currentVesselChangesCount", "customerSubscription.metadata.maxVesselChangesCount", "_subscriptionProductCode")
    public get canChangeVessels() {
        return this.isRightShipUser && this.customerSubscription && !this.customerSubscription.isExpired && this.customerSubscription.metadata.currentVesselChangesCount < this.customerSubscription.metadata.maxVesselChangesCount;
    }

    @computedFrom("customerSubscription", "customerSubscription.isExpired", "dataGridVM.itemsCollection.length", "dataGridVM.itemsCollection", "dataGridVM", "dataGridVM._specificIds", "dataGridVM._specificIds.length")
    get shouldDisableDelete(): boolean {
        if (this.customerSubscription && this.customerSubscription.isExpired) return true;

        if(!this.dataGridVM || !this.dataGridVM.getSelectedRowsCollection()) {
            return false;
        }
        else {
            if(this.dataGridVM.getSelectedRowsCollection().length > 0) {
                return false;
            }

            return !this.dataGridVM || !this.dataGridVM.getSelectedRowsCollection() || this.dataGridVM.getSelectedRowsCollection().length <= 0;
        }
    }

    @computedFrom("_subscriptionProductCode")
    get isInformationServiceSubscription() {
        return this._subscriptionProductCode == SubscriptionProductCodes.informationServices;
    }

    @computedFrom("customerSubscription", "customerSubscription.metadata", "customerSubscription.metadata.isFleetVessels")
    get accessToVesselsText() {
        return this.customerSubscription?.metadata?.isFleetVessels ? "All vessels under the DOC" : "All vessels in the platform";
    }

    activate(model: {customerId: number, subscription: CustomerSubscriptionModel, reloadSubscription: () => void, isRightShipUser: boolean, subscriptionProductCode: string}) {
        this.customerId = model.customerId;
        this.customerSubscription = model.subscription;
        this._reloadSubscription = model.reloadSubscription;
        this.isRightShipUser = model.isRightShipUser;
        this._subscriptionProductCode = model.subscriptionProductCode;
        this.isLimitedSubscription = this.customerSubscription?.subscriptionTypeId == SubscriptionTypeEnum.Limited || this.customerSubscription?.isExpired;
    }

    getCustomerSubscription() {
        this.customerSubscriptionService.getCustomerSubscription(this.customerId, this._subscriptionProductCode).then(response => {
            this.customerSubscription = response.customerSubscription;
            this.isLimitedSubscription = response?.customerSubscription?.subscriptionTypeId == SubscriptionTypeEnum.Limited || response?.customerSubscription?.isExpired;
            this.itemSource = this.getItemSource();
        });
    }

    filterKeyDown($event) {
        const e: KeyDownEvent = $event.detail;

        if (e.key == "Enter") {
            this.setInitialFilters();
            this.itemSource = this.getItemSource();
        }
    }

    addVessel() {
        if (!this.canAddVessels) return;

        if (this.numOfVessels >= this.customerSubscription.metadata.maxVesselsCount) {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.UpgradeSubscriptionDialog, { content: "To continue, you must upgrade your subscription as you have surpassed the maximum allowance for adding vessels or making changes to existing ones.", productCode: this._subscriptionProductCode }, true);
        }
        else {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.AddVesselToSubscriptionDialog, {
                onSaved: async () => {
                    this.itemSource = this.getItemSource();
                    this._reloadSubscription();
                },
                customerId: this.customerId,
                customerSubscription: this.customerSubscription,
                subscriptionProductCode: this._subscriptionProductCode
            }, true, false, false, "right");
        }
    }

    upgradeSubscription() {
        const url: string = this.subscriptionHelper.getUpgradeSubscriptionUrl(this._subscriptionProductCode);
        window.open(url, "_blank");
    }

    openChangeVesselDialog(vessel: any) {
        if (this.customerSubscription?.metadata?.maxVesselChangesCount <= this.customerSubscription?.metadata?.currentVesselChangesCount) {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.UpgradeSubscriptionDialog, { content: "You need to upgrade your subscription to make vessel changes.", productCode: this._subscriptionProductCode }, true);
        } else {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.ChangeVesselSubscriptionDialog,
                {
                    onSaved: () => {
                        this.itemSource = this.getItemSource();
                        this._reloadSubscription();
                    },
                    vesselId: vessel.id,
                    customerSubscription: this.customerSubscription,
                    customerId: this.customerId
                }, true, false, false, "right");
        }
    }

    getItemSource() {
        this.columns = this.getColumns();
        const sourceUrl = this.isRightShipUser ? `odata/CustomerVessels?customerId=${this.customerId}&productCode=${this._subscriptionProductCode}` : `odata/CustomerVessels?productCode=${this._subscriptionProductCode}`;
        return {
            sourceType: SourceType.ODataUrl,
            source: sourceUrl,
            columns: this.columns,
            initialFilters: this.initialFilters,
            initialSorting: this.initialSorting
        };
    }

    getColumns(): DataGridColumn[] {
        return [
            {
                dataType: DataType.CustomTemplate,
                value: `<check-box checked.bind="getRowItem() && getRowItem().isSelected"
                            changed.delegate="getDataGrid().selectRowInMultipleSelection(getRowItem())">
                        </check-box>`,
                name: "select",
                title: MobileHelper.isMobileDevice() ? "" : `<check-box class="check-box-select-all" checked.two-way="toggleAll"></check-box>`,
                parameter: {
                    item: null
                },
                instancePerRow: true,
                sortOrder: 1,
                columnOrder: -2,
                width: "50px",
                isHidden: this.isInformationServiceSubscription || !this.isRightShipUser,
                isSortable: false,
                getCellClass: () => {
                    return "selector-checkbox";
                }
            },
            {
                dataType: DataType.CustomTemplate,
                value: '<div class="row"><div class="column">' +
                    '<a class="link" href="/vessel/${item.vesselId}">${item.name}</a>' +
                    '<span if.bind="item.internationalMaritimeOrganizationNumber">IMO ${item.internationalMaritimeOrganizationNumber}</span>' +
                    '<span if.bind="item.vehicleIdentificationNumber">VIN ${item.vehicleIdentificationNumber}</span>' +
                    '<span if.bind="item.europeanNumberOfIdentification">ENI ${item.europeanNumberOfIdentification}</span>' +
                    '</div>' +
                    '<comp-icon class="icon-change margin-left-24" if.bind="canChangeVessels()" icon="arrow-reset" click.trigger="changeVessel(item)"></comp-icon>' +
                    '</div>',
                name: "name",
                title: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "Name",
                instancePerRow: true,
                sortOrder: 2,
                columnOrder: 2,
                isHidden: false,
                isSortable: true,
                parameter: {
                    item: null,
                    changeVessel: (item) => {
                        this.openChangeVesselDialog(item);
                    },
                    canChangeVessels: () => this.canChangeVessels
                }
            },
            {
                dataType: DataType.CustomTemplate,
                value: '<comp-safety-score score.bind="item.safetyRating" inspection.bind="item" safety-rating-indicative.bind="item.safetyRatingIndicative" safety-score-type.bind="item.safetyRatingType" incident-grace-periods.bind="getIncidentGracePeriods(item)"' +
                    'has-error.bind="item.hasError" is-condensed="true" unlock-feature-key="vessels" is-sanctioned-or-geopolitical-zone-or-flag-fake.bind="item.isSanctioned || item.isFlagFake" locked-feature.bind="lockedFeature" mask-data.bind="maskData(item.safetyRating)"></comp-safety-score>',
                name: "safetyRating",
                title: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "Safety Score",
                instancePerRow: true,
                sortOrder: 3,
                columnOrder: 3,
                isHidden: this._subscriptionProductCode == SubscriptionProductCodes.pscRiskIqInApp,
                isSortable: true,
                parameter: {
                    item: null,
                    lockedFeature: this.isLimitedSubscription,
                    maskData: (safetyRating) => {
                        return safetyRating === undefined;
                    },
                    getIncidentGracePeriods: (item) => {
                        return item.incidentGracePeriodJsonData ? JSON.parse(item.incidentGracePeriodJsonData) : [];
                    }
                },
                width: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "220px"
            },
            {
                dataType: DataType.CustomTemplate,
                value: '<comp-ghg-rating rating.bind="item.ghgRating" rating-plus.bind="item.isGhgRatingPlus" ' +
                    'is-verified.bind="item.isGhgRatingVerified" is-eexi-compliant.bind="item.isEexiCompliant" ' +
                    'eexi-compliance-type.bind="item.eexiComplianceTypeId" show-eexi-banner.bind="true" unlock-feature-key="vessels" locked-feature.bind="lockedFeature" mask-data.bind="maskData(item.ghgRating)"></comp-ghg-rating>',
                name: "ghgRatingWeight",
                title: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "GHG Rating",
                instancePerRow: true,
                sortOrder: 3,
                columnOrder: 3,
                isHidden: this._subscriptionProductCode == SubscriptionProductCodes.pscRiskIqInApp,
                isSortable: true,
                parameter: {
                    item: null,
                    lockedFeature: this.isLimitedSubscription,
                    maskData: (ghgRating) => {
                        return ghgRating === undefined;
                    }
                },
                width: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "140px"
            },
            {
                columnOrder: 4,
                sortOrder: 4,
                dataType: DataType.CustomTemplate,
                value: '<span>${item.shipType}</span>',
                name: 'shipType',
                title: 'Vessel Type',
                instancePerRow: true,
                isHidden: false,
                isSortable: true,
                parameter: {
                    item: null
                }
            },
            {
                columnOrder: 5,
                sortOrder: 5,
                dataType: DataType.CustomTemplate,
                value: "<span>${item.dwt.toLocaleString()}</span>",
                name: 'dwt',
                title: 'DWT',
                isSortable: true,
                instancePerRow: true,
                parameter: {
                    item: null
                },
            },
            {
                columnOrder: 6,
                sortOrder: 6,
                dataType: DataType.CustomTemplate,
                value: "<span>${item.age}y/o (${item.yearOfConstruction})</span>",
                // instancePerRow: true,
                name: 'age',
                title: 'Age',
                isSortable: true,
                instancePerRow: true,
                parameter: {
                    item: null
                },
            },
            {
                columnOrder: 7,
                sortOrder: 7,
                dataType: DataType.CustomTemplate,
                value: '<span>${item.shipStatus}</span>',
                name: 'shipStatus',
                title: 'Vessel Status',
                instancePerRow: true,
                isHidden: false,
                isSortable: true,
                parameter: {
                    item: null
                }
            },
            {
                columnOrder: 8,
                sortOrder: 8,
                dataType: DataType.DateTime,
                name: 'addedDate',
                title: 'Added on',
                isSortable: true,
                valueConverter: (date: Date | string) => date
                    ? moment(date).format('DD MMM YYYY')
                    : '',
            },
            {
                columnOrder: 9,
                sortOrder: 9,
                dataType: DataType.CustomTemplate,
                value: '<a class="link" click.trigger="delete(item)">Delete</a>',
                name: 'delete',
                title: 'Action',
                instancePerRow: true,
                isHidden: this.isInformationServiceSubscription || !this.isRightShipUser,
                isSortable: true,
                parameter: {
                    item: null,
                    delete: (item) => this.openDeleteDialog(item)
                }
            },
        ];
    }

    private setInitialFilters() {
        const initialFilters = [];
        initialFilters.push(QueryFilter.getQuery("name", this.filter?.toLowerCase(), "contains(tolower({0}), '{1}')", DataType.Default, true, false, 'vessel-search-filters', true, true))
        initialFilters.push(QueryFilter.getQuery("internationalMaritimeOrganizationNumber", this.filter?.trim(), "contains({0}, '{1}')", DataType.Default, false, false, 'vessel-search-filters', true, true))

        this.initialFilters = initialFilters;
    }

    filterChanged() {
        if ((this.filter && this.filter.length >= 5) || this.filter == "") {
            this.setInitialFilters();
            this.itemSource = this.getItemSource();
        }
    }

    openDeleteDialog(item?) {
        let items;

        if(item && item.vesselId) { //Single vessel delete
            items = [item];
        } 
        else { //Multiple vessels delete
            if (this.shouldDisableDelete) return;
            
            items = this.dataGridVM.getSelectedRowsCollection().map(x => x.item);
        }

        if(items.length > 0) {
            const model = new RightshipConfirmationDialogModel();
            model.confirm = "Yes, Delete vessel";
            model.cancel = "Cancel";
            model.confirmClass = "danger";

            model.messages.push('Selected vessel(s) will be deleted. Are you sure you want to delete?');
            model.title = "Confirm Delete Vessel(s)";

            model.onConfirm = () => {
                this.removeVessel(items.map(x => x.vesselId));
            }

            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.RightshipConfirmationDialog, model, true, true, false);
        }
    }

    removeVessel(vesselIds: number[]) {
        this.customerVesselsService.removeCustomerVessels({ customerId: this.customerId, productCode: this._subscriptionProductCode, vesselIds: vesselIds }).then(response => {
            if (response.validationResult && response.validationResult.hasError) 
                this.validationsClientService.showValidationErrors(response.validationResult);
            else
                this.getCustomerSubscription();
        }).catch(() => {
            this.notificationService.show("Failed to delete vessels", "", NotificationType.Error);
        });
    }
}